import React, { useState, useEffect } from "react";
import Seat from "./tables/shared/Seat";
import HeroSeat from "./tables/shared/HeroSeat";
import Board from './tables/shared/Board';
import BottomControls from './layout/BottomControls';
import GameHeader from './tables/shared/GameHeader';
import "./poker-table.css";

const SEATS = [
  { 
    id: "seat-1", 
    title: "BTN", 
    blinds: "97.5", 
    isDealer: true,
    bet: 0,
    showPotWon: false
  },
  { 
    id: "seat-2", 
    title: "SB", 
    blinds: "99.5", 
    isDealer: false,
    bet: 0,
    showPotWon: false
  },
  { 
    id: "seat-3", 
    title: "BB", 
    blinds: "99", 
    isDealer: false,
    bet: 0,
    showPotWon: false
  },
  { 
    id: "seat-4", 
    title: "LJ", 
    blinds: "100", 
    isDealer: false,
    bet: 0,
    showPotWon: false
  },
  { 
    id: "seat-5", 
    title: "HJ", 
    blinds: "100", 
    isDealer: false,
    bet: 0,
    showPotWon: false
  }
];

const ACTIVE_SEATS = ["seat-1"];

// Hero seat data
const HERO = {
  id: "hero",
  blinds: "89.5",
  isDealer: false,
  bet: 0,
  showPotWon: false,
  card1Value: "J",
  card1Suit: "diamonds",
  card2Value: "J",
  card2Suit: "hearts"
};

// Board state data
const BOARD_STATE = {
  flop1: { value: '', suit: '' },
  flop2: { value: '', suit: '' },
  flop3: { value: '', suit: '' },
  turn: { value: '', suit: '' },
  river: { value: '', suit: '' },
  isTurnRevealed: false,
  isRiverRevealed: false,
  preflopAction: 'SRP',
  heroPosition: 'LJ',
  villainPosition: 'BTN',
  potSize: '0'
};

const PokerTable = () => {
  // Track state of pot winners
  const [potWinners, setPotWinners] = useState(new Set());
  const [isPotVisible, setIsPotVisible] = useState(true);
  const [currentHand, setCurrentHand] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  // Effect to update pot visibility whenever winners change
  useEffect(() => {
    const hasAnyWinner = potWinners.size > 0 || HERO.showPotWon;
    setIsPotVisible(!hasAnyWinner);
  }, [potWinners]);

  // Handle pot won changes
  const handleSeatPotWonChange = (seatId, showPotWon) => {
    setPotWinners(prev => {
      const next = new Set(prev);
      if (showPotWon) {
        next.add(seatId);
      } else {
        next.delete(seatId);
      }
      return next;
    });
  };

  const handleHeroPotWonChange = (showPotWon) => {
    if (showPotWon) {
      setIsPotVisible(false);
    } else {
      // Only show pot if no other seats have won
      setIsPotVisible(potWinners.size === 0);
    }
  };

  const handleCancel = () => {
  };

  const handleBetClick = () => {
    if (isProcessing) return;
    
    setIsProcessing(true);

    setTimeout(() => {
      setCurrentHand(prev => prev + 1);
      setIsProcessing(false);
    }, 1000);
  };

  const handleCheckClick = () => {
    if (isProcessing) return;

    setIsProcessing(true);

    setTimeout(() => {
      setCurrentHand(prev => prev + 1);
      setIsProcessing(false);
    }, 1000);
  };

  const buttons = [
    {
      text: "Bet",
      className: `bet-button ${isProcessing ? 'opacity-50' : ''}`,
      onClick: handleBetClick
    },
    {
      text: "Check",
      className: `check-button ${isProcessing ? 'opacity-50' : ''}`,
      onClick: handleCheckClick
    }
  ];

  return (
    <div className="poker-table-container">
      <GameHeader 
        currentHand={currentHand}
        totalHands={10}
        onCancel={handleCancel}
      />
      
      <div className="game-content">
        <div className="table-content">
          <div className="table-area">
            <div className="outer-container">
              <div className="table-wrapper">
                <div className="div" />
                
                <Board 
                  {...BOARD_STATE}
                  isPotVisible={isPotVisible}
                />
                
                {SEATS.map(({ id, title, blinds, isDealer, bet, showPotWon }) => (
                  <Seat
                    key={id}
                    title={title}
                    blinds={blinds}
                    className={id}
                    isActive={ACTIVE_SEATS.includes(id)}
                    isDealer={isDealer}
                    bet={bet}
                    potSize={BOARD_STATE.potSize}
                    showPotWon={showPotWon}
                    onPotWonChange={(hasWon) => handleSeatPotWonChange(id, hasWon)}
                  />
                ))}
                
                <div className="hero-seat-wrapper">
                  <HeroSeat 
                    {...HERO}
                    potSize={BOARD_STATE.potSize}
                    onPotWonChange={handleHeroPotWonChange}
                  />
                </div>
              </div>
            </div>

            {currentHand <= 10 && (
              <div className="bottom-controls-wrapper">
                <BottomControls 
                  question="Based on this flop texture, is your hand a bet or check?"
                  buttons={buttons}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PokerTable;